import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { InputNumber as AntdInputNumber } from 'antd';
import { InputNumberProps as AntdInputNumberProps } from 'antd/lib/input-number';
import numeral from 'numeral';
import { isNil } from 'lodash-es';
import classNames from 'classnames';
import styles from './InputNumber.module.scss';
import { Nullable, Omit } from 'app-types';
import { ThemeContext } from 'app-themes';

interface InputNumberProps extends Omit<AntdInputNumberProps, 'onChange'> {
	alignLeft?: boolean;
	large?: boolean;
	small?: boolean;
	currencySymbol?: string;
	isPercentage?: boolean;
	onChange?: (value?: number) => void;
	ownValue?: Nullable<number | string>;
}

export class InputNumber extends React.Component<InputNumberProps> {
	inputRef = React.createRef<AntdInputNumber>();
	static contextType = ThemeContext;

	focus() {
		const { current } = this.inputRef;
		if (current) {
			current.focus();
		}
	}

	removeSpaceAndCommas = (value: any) => {
		const { precision } = this.props;
		if (typeof value === 'string' || typeof value === 'number') {
			if (precision === 0) {
				return value
					.toString()
					.replace('.', '')
					.replace(',', '');
			}
			return value
				.toString()
				.replace(' ', '')
				.replace(',', '.');
		}
		return value;
	};

	getCursorPosition = (): null | number => {
		const { current } = this.inputRef;
		if (current) {
			const domEl = findDOMNode(current) as Element;
			if (!isNil(domEl) && domEl.tagName) {
				const inp = domEl.querySelector('input');
				if (inp) {
					return inp.selectionStart;
				}
			}
		}
		return null;
	};

	removeExtraDots = (value: string = '') => {
		const dotsRegexp = RegExp('\\.', 'g');
		const dotsLength = (value.match(dotsRegexp) || []).length;
		if (dotsLength >= 2) {
			const cursorPosition = this.getCursorPosition();
			if (isNil(cursorPosition)) {
				return value;
			}
			const dotPosition = value.lastIndexOf('.');
			const position = cursorPosition > dotPosition ? value.indexOf('.') : dotPosition;
			const part1 = value.substring(0, position);
			const part2 = value.substring(position + 1, value.length);
			return part1 + part2;
		}
		return value;
	};

	fixDecimals = (value: string) => {
		value = this.removeExtraDots(value);
		const val = value.split('.')[0];
		const decimals = value.split('.')[1];
		if (decimals) {
			return val + '.' + decimals.substring(0, 2);
		}
		return value;
	};

	formatter = (val: any) => {
		const { isPercentage, currencySymbol } = this.props;
		const value = this.removeSpaceAndCommas(val);

		if (isPercentage && value) {
			return `${value}%`;
		}

		if (currencySymbol && value) {
			return `${currencySymbol} ${this.fixDecimals(this.removeCurrencyChar(value))}`;
		}

		return value;
	};

	removeCurrencyChar = (value: string) => {
		value = this.removeExtraDots(value);
		const { currencySymbol } = this.props;
		const regExpCurrencySymbol = new RegExp(`\\${currencySymbol}\\s?|(,*)`, 'g');
		return value.replace(regExpCurrencySymbol, '');
	};

	preventIf = (
		value: string,
		moreThen: number,
		defaultMoreThenValue: string = moreThen.toString()
	): string => {
		const valueToNumber = Number(value);
		if (!isNaN(valueToNumber) && valueToNumber > moreThen) {
			return defaultMoreThenValue;
		}
		return value;
	};

	parser = (val: any) => {
		const { isPercentage, currencySymbol } = this.props;
		const value = this.removeSpaceAndCommas(val);

		if (isPercentage) {
			const valueWithoutSymbol = value.replace('%', '');
			return this.preventIf(valueWithoutSymbol, 100);
		}

		if (currencySymbol) {
			return this.removeCurrencyChar(value);
		}

		return value;
	};

	getPrecision = () => {
		const { isPercentage, currencySymbol, precision } = this.props;

		if (isPercentage) {
			return 1;
		}

		if (currencySymbol) {
			return 3;
		}

		return precision;
	};

	getMax = () => {
		const { isPercentage, max } = this.props;

		if (isPercentage) {
			return 100;
		}

		return max;
	};

	onChangeHandler = (value: Nullable<number | string>) => {
		const { onChange } = this.props;
		if (onChange) {
			const numeralValue = numeral(value).value();
			if (value === '' || isNil(value) || isNil(numeralValue)) {
				onChange(undefined);
			} else {
				onChange(Number(numeralValue.toFixed(2)));
			}
		}
	};

	render() {
		const {
			className,
			alignLeft,
			large,
			small,
			min,
			value,
			defaultValue,
			isPercentage,
			ownValue,
			...restProps
		} = this.props;
		const theme = this.context;

		let overControlled = {};
		if ('ownValue' in this.props) {
			overControlled = {
				value: ownValue,
			};
		}
		return (
			<AntdInputNumber
				defaultValue={value || defaultValue}
				className={classNames(styles.inputNumber, className, {
					[styles.inputNumberAlignLeft]: alignLeft,
					[styles.inputNumberLarge]: large,
					[styles.inputNumberSmall]: small,
					[styles[`inputNumber-${theme}`]]: theme !== '',
				})}
				min={min || 0}
				formatter={this.formatter}
				parser={this.parser}
				precision={this.getPrecision()}
				max={this.getMax()}
				ref={this.inputRef}
				{...restProps}
				{...overControlled}
				onChange={this.onChangeHandler}
			/>
		);
	}
}

export default InputNumber;
